/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    BACKUP_DETAIL_PAGE_ENHANCED
 * Date:         2026-01-05
 * Modified By:  Cursor
 *
 * Description:
 * Enhanced backup detail page showing status, last backup, next backup, storage usage,
 * and action buttons as per business requirements. History table shown below.
 *
 * File Size Check:
 * - Current: ~180 lines
 * - Adding: ~80 lines
 * - Projected: ~260 lines
 * - Decision: Split if exceeds limit
 *
 * Related Screens / Modules:
 * - client/src/services/schedulerApi.ts
 * - server/controllers/admin/schedulerController.js
 */
/**
 * UAVTradeZone Modification Record
 * 
 * Change ID: PHASE_2_BACKUP_ADMIN_UI
 * Date: 2025-01-03
 * Modified By: Cursor
 * 
 * Description:
 * Admin page for backup management
 * Allows manual backup trigger and history viewing
 */

import React, { useRef, useState, useEffect } from 'react';
import { Table, Alert, Spinner, Card, Row, Col } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import schedulerApi from '../../../services/schedulerApi';
import './AdminBackupPage.scss';
import AdminButton from '../../../components/admin/AdminButton';
import RestoreModal from './components/RestoreModal';
/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    ADMIN_BUTTON_USAGE_BACKUP_PAGE
 * Date:         2026-01-07
 * Modified By:  Cursor
 *
 * Description:
 * Replaces hero and action buttons with AdminButton or approved btn-* tokens
 * so the backup admin UI uses the design-system button tokens.
 *
 * File Size Check:
 * - Current: 272
 * - Adding: ~20
 * - Projected: ~292
 * - Decision: Proceed
 *
 * Typography Compliance (UI files only):
 * - Utilities used: .u-P2-16-medium (via btn-black / btn-figma-1)
 * - Tokens used: --font-size-body-small, --line-height-body-small
 *
 * Related Files:
 * - client/src/components/admin/AdminButton.tsx
 * - client/src/features/admin/scheduler/AdminBackupPage.tsx
 */

interface DashboardData {
  backup: {
    status: string;
    lastBackupTimestamp: string | null;
    nextBackupTimestamp: string | null;
    nextBackupFrequency: string;
    storage: {
      usedGB: number;
      quotaGB: number;
      percent: number;
    };
  };
}

const AdminBackupPage: React.FC = () => {
  const navigate = useNavigate();
  const lastHistoryTokenRef = useRef<string | null>(null);
  // [2026-01-05] Backup detail: State for dashboard data and history
  const [dashboard, setDashboard] = useState<DashboardData | null>(null);
  const [backups, setBackups] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [dashboardLoading, setDashboardLoading] = useState(true);
  const [triggering, setTriggering] = useState(false);
  const [triggeringRestore, setTriggeringRestore] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [showRestoreModal, setShowRestoreModal] = useState(false);
  const [restoreTarget, setRestoreTarget] = useState<any | null>(null);

  // [2026-01-05] Backup detail: Load dashboard data and history on mount
  useEffect(() => {
    loadDashboard();
    loadBackupHistory({ silent: false });
  }, []);

  // [2026-01-31] Smart refresh: Poll a lightweight summary token and only reload
  // the full history table when something has changed.
  useEffect(() => {
    const POLL_INTERVAL_MS = 5000; // 5 seconds
    const id = setInterval(() => {
      checkBackupHistoryChanges();
    }, POLL_INTERVAL_MS);

    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadDashboard = async () => {
    try {
      setDashboardLoading(true);
      const response = await schedulerApi.fetchSchedulerDashboard();
      if (response.success && response.data) {
        setDashboard(response.data);
      }
    } catch (error: any) {
      console.error('Failed to load dashboard:', error);
    } finally {
      setDashboardLoading(false);
    }
  };

  const loadBackupHistory = async (opts: { silent: boolean }) => {
    try {
      if (!opts.silent) {
        setLoading(true);
      }
      const response = await schedulerApi.getBackupHistory({ limit: 20 });
      setBackups(response.data.backups);
    } catch (error: any) {
      setMessage({ type: 'error', text: error.message || 'Failed to load backup history' });
    } finally {
      if (!opts.silent) {
        setLoading(false);
      }
    }
  };

  const checkBackupHistoryChanges = async () => {
    try {
      const response = await schedulerApi.getBackupHistorySummary();
      const token = response?.data?.token || null;
      if (!token) return;

      if (lastHistoryTokenRef.current !== token) {
        lastHistoryTokenRef.current = token;
        // [2026-01-31] Smart refresh: Avoid spinner during background refresh
        loadBackupHistory({ silent: true });
      }
    } catch {
      // [2026-01-31] Smart refresh: Silent failure; keep current table
    }
  };

  const handleTriggerBackup = async () => {
    // [2025-01-03] Admin UI: Trigger manual backup
    try {
      setTriggering(true);
      setMessage(null);

      await schedulerApi.triggerBackup({
        include_files: true
      });

      setMessage({ type: 'success', text: 'Backup job scheduled successfully. It will run within 5 minutes.' });
      
      // Reload dashboard and history after 10 seconds
      setTimeout(() => {
        loadDashboard();
        loadBackupHistory({ silent: false });
      }, 10000);

    } catch (error: any) {
      setMessage({ type: 'error', text: error.message || 'Failed to trigger backup' });
    } finally {
      setTriggering(false);
    }
  };

  const formatSize = (bytes: number) => {
    // [2025-01-03] Admin UI: Format file size
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  const getBackupFileNameForRestore = (backup: any): string | null => {
    return backup?.r2?.file_name || backup?.google_drive?.file_name || null;
  };

  const isRestoreableBackupFileName = (fileName: string | null): boolean => {
    if (!fileName) return false;
    // Expected by backend: backup-{env}-{YYYY-MM-DD-HHmmss}.tar.gz
    return /^backup-(dev|test|prod)-\d{4}-\d{2}-\d{2}-\d{6}\.tar\.gz$/.test(fileName);
  };

  const canRestoreBackup = (backup: any): boolean => {
    if (!backup) return false;
    if (backup.status !== 'success') return false;
    const name = getBackupFileNameForRestore(backup);
    return isRestoreableBackupFileName(name);
  };

  const openRestoreModal = (backup: any) => {
    setRestoreTarget(backup);
    setShowRestoreModal(true);
  };

  const closeRestoreModal = () => {
    if (triggeringRestore) return;
    setShowRestoreModal(false);
    setRestoreTarget(null);
  };

  const handleScheduleRestore = async (opts: { restoreAll: boolean; collections: string[] }) => {
    try {
      if (!restoreTarget) return;
      const backupFileName = getBackupFileNameForRestore(restoreTarget);
      if (!backupFileName) {
        setMessage({ type: 'error', text: 'No backup file name available for this backup.' });
        return;
      }

      setTriggeringRestore(true);
      setMessage(null);

      await schedulerApi.triggerRestore({
        backup_file_name: backupFileName,
        collections: opts.restoreAll ? null : opts.collections,
      });

      setMessage({
        type: 'success',
        text:
          'Restore job scheduled. It will run in the scheduler queue. Pre-restore collections will be kept for rollback.',
      });

      closeRestoreModal();

      // Refresh dashboard + history shortly after scheduling
      setTimeout(() => {
        loadDashboard();
        loadBackupHistory({ silent: false });
      }, 1500);
    } catch (error: any) {
      setMessage({ type: 'error', text: error.message || 'Failed to schedule restore' });
    } finally {
      setTriggeringRestore(false);
    }
  };

  const formatDate = (dateString: string | null, fallback = "N/A") => {
    if (!dateString) return fallback;
    return new Date(dateString).toLocaleString();
  };

  const formatStatus = (status?: string) => {
    if (!status) return "Status: N/A";
    if (status === "active") return "Status: 🟢 Active";
    return `Status: ${status}`;
  };

  return (
    <div className="admin-backup-page">
      <h2>Backup & Restore</h2>
      <p className="text-muted mb-4">Automated database backup and restore management</p>

      {message && (
        <Alert variant={message.type === 'success' ? 'success' : 'danger'} dismissible onClose={() => setMessage(null)}>
          {message.text}
        </Alert>
      )}

      {/* [2026-01-05] Backup detail: Display status and information card */}
      <Card className="mb-4">
        <Card.Body>
          {dashboardLoading ? (
            <div className="text-center py-4">
              <Spinner animation="border" />
            </div>
          ) : dashboard ? (
            <>
              <Row className="mb-3">
                <Col>
                  <div className="mb-2">
                    <strong>{formatStatus(dashboard.backup.status)}</strong>
                  </div>
                  <div className="mb-2">
                    <strong>Last Backup:</strong> {formatDate(dashboard.backup.lastBackupTimestamp)} · Success
                  </div>
                  <div className="mb-2">
                    <strong>Next Backup:</strong> {formatDate(dashboard.backup.nextBackupTimestamp)} · {dashboard.backup.nextBackupFrequency || "Daily"}
                  </div>
                  <div>
                    <strong>Storage Used:</strong> {dashboard.backup.storage.usedGB} GB / {dashboard.backup.storage.quotaGB} GB ({Math.round(dashboard.backup.storage.percent)}%)
                  </div>
                </Col>
              </Row>
              <div className="d-flex gap-2 flex-wrap">
                <AdminButton intent="primary" onClick={handleTriggerBackup} disabled={triggering}>
                  {triggering ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      Scheduling...
                    </>
                  ) : (
                    'Backup Now'
                  )}
                </AdminButton>
                <AdminButton intent="secondary" onClick={() => navigate('/admin/scheduler/backup-files')}>
                  Backup Files / Restore
                </AdminButton>
                <AdminButton intent="secondary">Settings</AdminButton>
              </div>
            </>
          ) : (
            <Alert variant="warning">Unable to load backup status.</Alert>
          )}
        </Card.Body>
      </Card>

      {/* [2026-01-05] Backup detail: History section */}
      <Card id="backup-history">
        <Card.Body>
          <Card.Title>Backup History</Card.Title>
          
          {loading ? (
            <div className="text-center py-4">
              <Spinner animation="border" />
            </div>
          ) : backups.length === 0 ? (
            <Alert variant="info">No backup history found.</Alert>
          ) : (
            <Table striped bordered hover responsive>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Type</th>
                  <th>Environment</th>
                  <th>Size</th>
                  <th>Collections</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {backups.map((backup) => (
                  <tr key={backup._id}>
                    <td>{formatDate(backup.timestamp)}</td>
                    <td className="text-capitalize">{backup.backup_type}</td>
                    <td className="text-uppercase">{backup.environment}</td>
                    <td>{formatSize(backup.r2?.file_size_bytes || backup.google_drive?.file_size_bytes || 0)}</td>
                    <td>{Object.keys(backup.collections || {}).length}</td>
                    <td>
                      {backup.status ? (
                        backup.status === 'running' ? (
                          <span className="text-info">⏳ Running</span>
                        ) : backup.status === 'pending' ? (
                          <span className="text-muted">● Pending</span>
                        ) : backup.status === 'success' ? (
                          backup.verification?.status === 'passed' ? (
                            <span className="text-success">✓ Verified</span>
                          ) : (
                            <span className="text-success">✓ Success</span>
                          )
                        ) : backup.status === 'failed' ? (
                          <span className="text-danger">✖ Failed</span>
                        ) : (
                          <span>{backup.status}</span>
                        )
                      ) : (
                        (backup.verification?.status === 'passed') ? (
                          <span className="text-success">✓ Verified</span>
                        ) : (
                          <span className="text-warning">⚠ Unverified</span>
                        )
                      )}
                    </td>
                    <td>
                      <div className="d-flex gap-2 flex-wrap">
                        {canRestoreBackup(backup) && (
                          <AdminButton
                            intent="danger"
                            size="sm"
                            onClick={() => openRestoreModal(backup)}
                          >
                            Restore
                          </AdminButton>
                        )}
                        {backup.google_drive?.web_view_link && (
                          <a
                            href={backup.google_drive.web_view_link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn-figma-1 btn-sm"
                          >
                            View in Drive
                          </a>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      <RestoreModal
        show={showRestoreModal}
        backup={restoreTarget}
        isScheduling={triggeringRestore}
        onCancel={closeRestoreModal}
        onRestore={handleScheduleRestore}
      />
    </div>
  );
};

export default AdminBackupPage;